import dynamic from 'next/dynamic';
import { toast } from 'react-toastify';
import { Fragment, useEffect, useRef, useState } from 'react';
import DiscountField from '@/components/ticketingNew/components/DiscountField';
import { OrderStageEnum, TicketPaymentType } from '@/components/ticketingNew/pages/Order';
import {
  infoModalDataState,
  orderedTicketsState,
  ticketingDataState,
  translationDataState,
  useSetRecoilState,
  useRecoilValue,
  useRecoilState,
  redirectTicketsState,
  tourConfigsState,
} from '../../../../store';
import { DurationType } from '@/components/ticketingNew/types';
import { sendToGA } from '@/components/util/analytic';
import { validateEmail } from '@/components/util/all';
import TreedisPublicDAO from '@/api/TreedisPublicDAO';
import { EInfoModal } from '@/components/ticketingNew/components/InfoModal';
import { ticketingTypes } from '@/components/util';
import { initialCurrency } from '@/components/ticketingNew/static';
import { MainWrap } from './styles';
import PayPalPaymentButton from '@/components-new/eCommerce/PayPalPaymentButton';

const StripePaymentButton = dynamic(() => import('@/components/ticketingNew/components/StripePaymentButton'), {
  ssr: false,
});
interface IOrderDetailsBlock {
  match: any;
  tourId: number;
  gameId: number;
  matterId: string;
  participants: any[];
  setOrderLink: (link: string) => void;
  setOrderStage: (stage: OrderStageEnum) => void;
  handlePaymentSuccess: () => void;

  initialTourType?: string;
  initialTicketId?: number;
}
type TTicketingType = 'ticketingToursByYourself' | 'ticketingGameTour' | 'ticketingGuidedTour';

const OrderDetailsBlock = (props: IOrderDetailsBlock) => {
  const { participants, setOrderLink, setOrderStage, handlePaymentSuccess, match, tourId, matterId, initialTourType, initialTicketId, gameId } = props;
  const ticketing = useRecoilValue(ticketingDataState);
  const { ticketingGeneral } = ticketing;
  const {
    plane_name = 'Plane name',
    available_for = 'Available for',
    private_note = 'private Note',
    ticket = 'ticket',
    grand_total = 'Grand Total',
    legal_checkbox_2 = 'Legal checkbox 2',
    legal_checkbox_1 = '*I agree terms & conditions',
    proceed_to_payment = 'Proceed to payment',

    devices = 'devices',
    day = 'day',
    days = 'days',
    hour = 'hour',
    hours = 'hours',
    min = 'min',
    mins = 'mins',
    from_purchase = 'from purchase',
    from_login = 'from login',
    max_participants = 'Max. participants',
    tour_language = 'Tour language',
    wrong_discount_val = 'wrong discount value',
    user = 'user',
    email = 'email',
    name = 'name',
    is_required = 'is required',
    is_not_valid = 'is not valid',
    check_out = 'Check Out',
    tickets_order_was_cancelled = 'Tickets order was cancelled',
    tickets_order_error = 'Tickets order error.',
  } = useRecoilValue(translationDataState);

  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  // ----------- order data fields --------------
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  // ----------- order data fields end --------------
  const [ticketOrderSent, setTicketOrderSent] = useState(false);
  // ticketType: "gameTour"
  const tourType = match.params.tourType || initialTourType?.replace('gameTour', 'experienceTour');
  const ticketId = match.params.ticketId || initialTicketId;
  const tourTypeKey = ticketingTypes?.find((tt: any) => tt.tourType === tourType);
  const ticketingType: TTicketingType = tourTypeKey.key as TTicketingType;
  const currentTicket = ticketId ? ticketing[tourTypeKey?.key].find((ticket: any) => ticket.id === parseInt(ticketId)) : null;
  const currencySymbol = initialCurrency.find((cur) => cur.id === ticketingGeneral.currencyId);
  const orderId = useRef(null);
  const orderBtn = useRef(null);
  const [infoModalData, setInfoModalData] = useRecoilState(infoModalDataState);
  const setTicketOrderResult = useSetRecoilState(orderedTicketsState);
  const [redirectTickets, setRedirectTickets] = useRecoilState(redirectTicketsState);
  const { rtl } = useRecoilValue(tourConfigsState);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    (async () => {
      if (redirectTickets) {
        console.log('redirectTickets', redirectTickets);
        const finishOrderRes = await TreedisPublicDAO.finishOrderTickets({
          /// TODO add transaction to validate
          orderCode: redirectTickets.orderCode,
          status: redirectTickets.status,
          pelecardStatusCode: redirectTickets.PelecardStatusCode,
          matterId,
        });
        if (finishOrderRes && finishOrderRes.message.includes('Success')) {
          if (finishOrderRes.status === 'success') {
            const ticketsString = redirectTickets.data;
            if (ticketsString) {
              sendToGA('event', {
                eventCategory: 'Ticketing',
                eventAction: 'purchase',
                eventLabel: tourType,
              });
              const tickets = JSON.parse(ticketsString);
              setTicketOrderResult({
                ...finishOrderRes?.order,
                tickets,
              });
              handlePaymentSuccess();
            }
          } else if (finishOrderRes.status === 'cancel') {
            setInfoModalData({
              ...infoModalData,
              isOpen: true,
              message: tickets_order_was_cancelled,
              mode: EInfoModal.error,
            });
          } else if (finishOrderRes.status === 'error') {
            setInfoModalData({
              ...infoModalData,
              isOpen: true,
              message: `${tickets_order_error} ${redirectTickets.PelecardStatusCode ? `PelecardStatusCode - ${redirectTickets.PelecardStatusCode}` : ''}`,
              mode: EInfoModal.error,
            });
          }
        }
      }
      setRedirectTickets(null);
    })();
  }, [redirectTickets]);

  useEffect(() => {
    if (!ticketingGeneral?.paymentType) {
      setInfoModalData({
        ...infoModalData,
        isOpen: true,
        message: 'Looks like ticketing payment method not set or missing. Please check ticketing -> Payment API on admin panel',
      });
    }
  }, []);

  const applyDiscounts = (value: number): number => {
    let result = value;

    if (!appliedDiscount) return result;
    if (appliedDiscount.discountType === 'currency') {
      if (appliedDiscount.amount > 0) result -= appliedDiscount.amount;
      else setInfoModalData({ ...infoModalData, isOpen: true, message: wrong_discount_val, mode: EInfoModal.error });
    } else if (appliedDiscount.discountType === 'percent') {
      if (appliedDiscount.amount > 0 && appliedDiscount.amount <= 100) result -= (result * appliedDiscount.amount) / 100;
      else setInfoModalData({ ...infoModalData, isOpen: true, message: wrong_discount_val, mode: EInfoModal.error });
    }
    if (result < 0) return 0;
    return result;
  };
  const totalPrice = currentTicket ? parseFloat(applyDiscounts(participants.length * currentTicket.ticketPrice).toFixed(2)) : 0;

  const _validateUsers = (noToast = false) => {
    const errors = [];
    if (ticketingGeneral.legalCheckbox1Required && !checkbox1) errors.push(`${legal_checkbox_1} ${is_required}`);
    if (ticketingGeneral.legalCheckbox2Required && !checkbox2) errors.push(`${legal_checkbox_2} ${is_required}`);

    participants.forEach((ticket: any, num: number) => {
      if (!ticket.email.length) {
        errors.push(`${user} ${num + 1} ${email} ${is_required}`);
      } else if (!validateEmail(ticket.email)) {
        errors.push(`${user} ${num + 1} ${email} ${is_not_valid}`);
      }
      if (!ticket.fullName) errors.push(`${user} ${num + 1} ${name} ${is_required}`);
    });

    if (errors.length) {
      if (!noToast) toast.error(errors.join('\n'));
      return 1;
    }
    return 0;
  };

  const handleProceedPayment = async () => {
    try {
      if (orderBtn?.current?.disabled) return;
      // validation here
      if (_validateUsers()) return;
      setTicketOrderSent(true);
      const body = {
        paymentType: ticketingGeneral.paymentType,
        ticketType: tourType.replace('experienceTour', 'gameTour'),
        tourId,
        matterId,
        participants: participants.map((participant) => {
          // fullName: '', email: '', isGift: false
          let { fullName, email, isGift } = participant;
          if (!fullName && !email && !isGift && participants[0]) {
            fullName = participants[0].fullName;
            email = participants[0].email;
            isGift = participants[0].isGift;
          }
          return { name: fullName, email, gift: isGift };
        }),
        selectedTicketId: currentTicket?.id,
        discountCode: appliedDiscount?.codeName,

        goodUrl: location.origin,
        cancelUrl: location.origin,
        errorUrl: location.origin,
        amount: totalPrice,
        // notificationGoodMail,
        // notificationErrorMail,
        // notificationFailMail,
        currency: currencySymbol?.code || 'USD',
        // language,
      };

      const { data } = await TreedisPublicDAO.createTicketOrder(body);
      const { order } = data;
      orderId.current = order?.id;
      if (ticketingGeneral.paymentType === TicketPaymentType.Pelecard || Number(totalPrice) === 0) {
        const ticketPayRes: any = await TreedisPublicDAO.payTickets(ticketingGeneral.paymentType || TicketPaymentType.NoPayment, {
          ...body,
          orderId: order.id,
        });
        if (ticketPayRes && ticketPayRes.message.includes('Success')) {
          if (ticketPayRes.ticketsWithoutCost) {
            console.log('ticketPayRes', ticketPayRes);
            setTicketOrderResult({ ...ticketPayRes, gameId });
            handlePaymentSuccess();
          } else {
            setOrderLink(ticketPayRes.url);
            setOrderStage(OrderStageEnum.payment);
          }
        }
      }
      setTicketOrderSent(false);
      return {
        ticketOrderId: order.id,
        name: participants[0].fullName,
        email: participants[0].email,
      };
    } catch (err) {
      setInfoModalData({ ...infoModalData, isOpen: true, message: err.message, mode: EInfoModal.error });
    } finally {
      setTicketOrderSent(false);
    }
  };
  const cancelOrder = () => {
    setInfoModalData({ ...infoModalData, isOpen: true, message: 'Error paypal payment', mode: EInfoModal.error });
  };
  const paypalCreateOrder = async (data: any, actions: any) => {
    const { ticketOrderId } = await handleProceedPayment();
    console.log('ticketOrderId', ticketOrderId);
    if (!ticketOrderId) throw new Error('No order ID');
    orderId.current = ticketOrderId;
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: totalPrice,
            currency_code: currencySymbol.code,
          },
        },
      ],
    });
  };
  const paypalApproveOrder = async (data: any, actions: any) => {
    const ticketPayRes: any = await TreedisPublicDAO.payTickets(ticketingGeneral.paymentType, {
      data: {
        orderId: orderId.current,
        paymentId: data.orderID,
      },
    });
    if (!ticketPayRes)
      return setInfoModalData({
        ...infoModalData,
        isOpen: true,
        message: `Error paypal payment - orderId: ${data.orderID}`,
        mode: EInfoModal.error,
      });
    console.log('ticketPayRes', ticketPayRes);
    setTicketOrderResult({ ...ticketPayRes, gameId });
    handlePaymentSuccess();
  };
  const stripeCreateOrder = async () => {
    try {
      const { ticketOrderId, name, email } = await handleProceedPayment();
      if (!ticketOrderId) throw new Error();
      return {
        id: ticketOrderId,
        totalPrice,
        currency: currencySymbol.code,
        name,
        email,
        tourId,
      };
    } catch (e) {
      return null;
    }
  };
  const approveOrder = async () => {
    const ticketPayRes: any = await TreedisPublicDAO.payTickets(ticketingGeneral.paymentType, {
      data: {
        orderId: orderId.current,
      },
    });
    console.log('ticketPayRes', ticketPayRes);
    if (!ticketPayRes) return;
    setTicketOrderResult({ ...ticketPayRes, gameId });
    handlePaymentSuccess();
  };

  const durationLabels = [
    [[day], [days]],
    [[hour], [hours]],
    [[min], [mins]],
  ];
  const formatTicketTime = (duration: number) => {
    let durtionMinLeft = duration;
    const durationDays = parseInt(String(durtionMinLeft / (24 * 60)), 10);

    durtionMinLeft -= durationDays * 60 * 24;
    const durationHours = parseInt(String(durtionMinLeft / 60), 10);
    durtionMinLeft -= durationHours * 60;

    return [durationDays, durationHours, durtionMinLeft];
  };

  const { durationType, limitDevices, duration, maxParticipants, tourLanguage, ticketPrice } = currentTicket || {};
  const pillsList = [];
  if (durationType) {
    pillsList.push(
      <div className="bl-ticket-order-pill" key="durationType">
        <img src="/ticketing/clock.svg" alt="clock-icon" />
        {formatTicketTime(duration).map((durationPart: number, num: number) => {
          if (durationPart) {
            return (
              <Fragment key={num}>
                {durationPart}
                <span>{durationPart === 1 ? durationLabels[num][0] : durationLabels[num][1]}</span>
                &nbsp;
              </Fragment>
            );
          }
          return null;
        })}{' '}
        {durationType === DurationType.from_purchase ? from_purchase : from_login}
      </div>,
    );
  }
  if (limitDevices) {
    pillsList.push(
      <div className="bl-ticket-order-pill" key="limitDevices">
        <img src="/ticketing/desktopMobile.svg" alt="desktop-mobile-icon" /> {limitDevices} {devices}
      </div>,
    );
  }
  if (maxParticipants) {
    pillsList.push(
      <div className="bl-ticket-order-pill" key="maxParticipants">
        <i className="fas fa-user-friends" />
        {max_participants} {maxParticipants}
      </div>,
    );
  }
  if (tourLanguage) {
    pillsList.push(
      <div className="bl-ticket-order-pill" key="tourLanguage">
        <i className="fal fa-globe" />
        {tour_language} {tourLanguage}
      </div>,
    );
  }
  if (ticketPrice) {
    pillsList.push(
      <div className="bl-ticket-order-pill" key="ticketPrice">
        <i className="fal fa-tag" />
        {ticketPrice}
        {currencySymbol.symbol}
      </div>,
    );
  }

  return (
    <MainWrap className="bl-ticket-order" rtl={rtl}>
      <div className="bl-ticket-order-title">{plane_name}</div>
      <div className="bl-ticket-order-subTitle">{available_for}:</div>
      <div className="bl-ticket-order-pills">{pillsList}</div>
      <div className="bl-ticket-order-private-note-wrap">
        <div className="bl-ticket-order-pnote-header">{private_note}</div>
        <div className="bl-ticket-order-pnote-content" dangerouslySetInnerHTML={{ __html: currentTicket?.description }} />
      </div>
      <div className="bl-ticket-order-price">
        <div className="bl-ticket-order-price-title">
          {participants.length} {ticket} X {currentTicket?.ticketPrice} {currencySymbol.code}
        </div>
        <hr className="bl-ticket-order-price-line" />
        <div className="bl-ticket-order-price-cost">
          {participants.length * currentTicket?.ticketPrice || 0}
          {currencySymbol.symbol}
        </div>
      </div>
      {currentTicket && (
        <DiscountField tourId={tourId} ticketId={ticketId} ticketingType={ticketingType} currencySign={currencySymbol.symbol} onDiscountApply={setAppliedDiscount} />
      )}
      <hr className="bl-ticket-order-after-disc-line" />
      <div className="bl-ticket-order-gtotal">
        <div className="bl-ticket-order-gtotal-title">{grand_total}</div>
        <div className="bl-ticket-order-gtotal-price">
          {totalPrice}
          {currencySymbol.symbol}
        </div>
      </div>
      <div className="bl-ticket-order-checkboxes">
        <label>
          <input type="checkbox" checked={checkbox1} onChange={() => setCheckbox1(!checkbox1)} />
          <a href={ticketingGeneral.termsAndConditionsUrl} target="_blank" rel="noreferrer">
            {legal_checkbox_1}
          </a>
        </label>
        <label>
          <input type="checkbox" checked={checkbox2} onChange={() => setCheckbox2(!checkbox2)} />
          {legal_checkbox_2}
        </label>
      </div>
      {ticketingGeneral && currentTicket && (ticketingGeneral.paymentType === TicketPaymentType.Pelecard || !ticketingGeneral.paid || totalPrice === 0) && (
        <button ref={orderBtn} disabled={ticketOrderSent} className="bl-ticket-order-pay-btn" onClick={handleProceedPayment}>
          <i className="fas icon" />
          {proceed_to_payment}
        </button>
      )}
      {!_validateUsers(true) && ticketingGeneral && currentTicket && ticketingGeneral.paymentType === TicketPaymentType.Stripe && totalPrice !== 0 && (
        <StripePaymentButton translation={{ check_out }} createOrder={stripeCreateOrder} settings={ticketingGeneral} approveOrder={approveOrder} />
      )}
      {!_validateUsers(true) &&
        ticketingGeneral?.apiUsername &&
        currencySymbol.code &&
        currentTicket &&
        ticketingGeneral.paymentType === TicketPaymentType.Paypal &&
        totalPrice !== 0 && (
          <div className="ch-btn">
            <PayPalPaymentButton
              currency={currencySymbol.code}
              clientPaypalId={ticketingGeneral?.apiUsername}
              createOrder={paypalCreateOrder}
              onApprove={paypalApproveOrder}
              onError={cancelOrder}
            />
          </div>
        )}
    </MainWrap>
  );
};

export default OrderDetailsBlock;
